import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Upload, Eye, Edit3, Columns2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import BlockRenderer from "../../components/BlockRenderer";
import RichTextEditor from "../../components/RichTextEditor";
import { plainToHtml } from "../../components/RichText";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

// Re-uses same simple block editor patterns as AdminPageEditor
const BLOCK_TYPES = [
  { value: "heading", label: "Heading" },
  { value: "text", label: "Text paragraph" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video (YouTube / Vimeo)" },
  { value: "quote", label: "Quote" },
];

const emptyBlock = (type) => {
  const id = Math.random().toString(36).slice(2, 10);
  switch (type) {
    case "heading": return { id, type, data: { text: "New heading", level: "h2" } };
    case "text": return { id, type, data: { content: "Write your content..." } };
    case "image": return { id, type, data: { path: "", caption: "", alt: "" } };
    case "video": return { id, type, data: { embed_url: "", caption: "" } };
    case "quote": return { id, type, data: { text: "Quote", author: "" } };
    default: return { id, type, data: {} };
  }
};

function CoverPicker({ value, onChange }) {
  const ref = useRef(null);
  const [up, setUp] = useState(false);
  const onUp = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUp(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.storage_path);
      toast.success("Cover uploaded");
    } catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); }
    finally { setUp(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div>
      <Label>Cover image</Label>
      <div className="flex items-start gap-4 mt-2">
        {value ? <img src={fileUrl(value)} alt="" className="w-44 h-28 object-cover border" /> :
          <div className="w-44 h-28 border border-dashed bg-neutral-50 flex items-center justify-center text-xs text-neutral-400">No cover</div>}
        <div className="space-y-2">
          <input type="file" accept="image/*" ref={ref} className="hidden" onChange={onUp} />
          <button type="button" onClick={() => ref.current?.click()} disabled={up} className="btn-outline-burgundy text-xs px-4 py-2">
            <Upload size={12} /> {up ? "Uploading..." : "Upload cover"}
          </button>
          {value && <button onClick={() => onChange("")} className="block text-xs text-neutral-500 hover:text-red-600">Remove</button>}
        </div>
      </div>
    </div>
  );
}

function BlockEditor({ block, onChange, onDelete, onMoveUp, onMoveDown }) {
  const update = (patch) => onChange({ ...block, data: { ...block.data, ...patch } });
  const fileRef = useRef(null);
  const [up, setUp] = useState(false);
  const onImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUp(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ path: data.storage_path });
      toast.success("Image uploaded");
    } catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); }
    finally { setUp(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="block-card">
      <div className="flex items-center justify-between mb-4">
        <span className="label-eyebrow">{block.type}</span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} className="p-1.5 hover:bg-neutral-100 rounded"><ArrowUp size={14} /></button>
          <button onClick={onMoveDown} className="p-1.5 hover:bg-neutral-100 rounded"><ArrowDown size={14} /></button>
          <button onClick={onDelete} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded"><Trash2 size={14} /></button>
        </div>
      </div>

      {block.type === "heading" && (
        <div className="space-y-3">
          <Input value={block.data.text || ""} onChange={e => update({ text: e.target.value })} />
          <Select value={block.data.level || "h2"} onValueChange={v => update({ level: v })}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="h2">H2</SelectItem>
              <SelectItem value="h3">H3</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {block.type === "text" && (
        <div>
          <RichTextEditor
            value={plainToHtml(block.data.content || "")}
            onChange={(html) => update({ content: html })}
          />
        </div>
      )}
      {block.type === "image" && (
        <div className="space-y-3">
          <div className="flex items-start gap-4">
            {block.data.path ? <img src={fileUrl(block.data.path)} alt="" className="w-32 h-24 object-cover border" /> :
              <div className="w-32 h-24 border border-dashed bg-neutral-50 flex items-center justify-center text-xs text-neutral-400">No image</div>}
            <div>
              <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onImage} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={up} className="btn-outline-burgundy text-xs px-4 py-2">
                <Upload size={12} /> {up ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
          <Input placeholder="Caption" value={block.data.caption || ""} onChange={e => update({ caption: e.target.value })} />
        </div>
      )}
      {block.type === "video" && (
        <div className="space-y-3">
          <Input placeholder="YouTube/Vimeo URL" value={block.data.embed_url || ""} onChange={e => update({ embed_url: e.target.value })} />
          <Input placeholder="Caption" value={block.data.caption || ""} onChange={e => update({ caption: e.target.value })} />
        </div>
      )}
      {block.type === "quote" && (
        <div className="space-y-3">
          <Textarea value={block.data.text || ""} onChange={e => update({ text: e.target.value })} rows={3} />
          <Input placeholder="Author" value={block.data.author || ""} onChange={e => update({ author: e.target.value })} />
        </div>
      )}
    </div>
  );
}

export default function AdminBlogEditor() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [addType, setAddType] = useState("text");
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("split");

  useEffect(() => {
    api.get("/blog", { params: { published_only: false } }).then(r => {
      const p = (r.data || []).find(x => x.id === postId);
      if (!p) { toast.error("Not found"); navigate("/admin/blog"); return; }
      setPost(p);
    });
  }, [postId, navigate]);

  if (!post) return <div className="p-10 text-neutral-500">Loading…</div>;

  const updateField = (k, v) => setPost({ ...post, [k]: v });
  const updateBlock = (i, b) => { const n = [...post.blocks]; n[i] = b; setPost({ ...post, blocks: n }); };
  const addBlock = () => setPost({ ...post, blocks: [...(post.blocks || []), emptyBlock(addType)] });
  const deleteBlock = (i) => { const n = [...post.blocks]; n.splice(i, 1); setPost({ ...post, blocks: n }); };
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= post.blocks.length) return;
    const n = [...post.blocks]; [n[i], n[j]] = [n[j], n[i]]; setPost({ ...post, blocks: n });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/blog/${post.id}`, {
        slug: post.slug, title: post.title, excerpt: post.excerpt,
        cover_image: post.cover_image, published: post.published, blocks: post.blocks,
      });
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6 sticky top-14 lg:top-0 bg-[#F7F7F7] z-30 py-3 -my-3 flex-wrap gap-3">
        <div>
          <Link to="/admin/blog" className="label-eyebrow text-[#6B1F2A] inline-flex items-center gap-2 mb-2"><ArrowLeft size={12} /> Back to blog</Link>
          <h1 className="font-serif-display text-3xl text-[#1a1a1a]">Edit post</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center bg-white border border-neutral-200 rounded-md p-0.5 mr-2">
            <button onClick={() => setViewMode("edit")} className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition-colors ${viewMode === "edit" ? "bg-[#6B1F2A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}><Edit3 size={12} /> Edit</button>
            <button onClick={() => setViewMode("split")} className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition-colors ${viewMode === "split" ? "bg-[#6B1F2A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}><Columns2 size={12} /> Split</button>
            <button onClick={() => setViewMode("preview")} className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition-colors ${viewMode === "preview" ? "bg-[#6B1F2A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}><Eye size={12} /> Preview</button>
          </div>
          <Link to={`/blog/${post.slug}`} target="_blank" className="btn-outline-burgundy"><Eye size={14} /> Live</Link>
          <button onClick={save} disabled={saving} className="btn-primary-burgundy disabled:opacity-60" data-testid="save-post-btn">{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>

      <div className={`grid gap-6 ${viewMode === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {viewMode !== "preview" && (
          <div className="min-w-0 max-w-3xl">
            <div className="bg-white border border-neutral-200 p-6 mb-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={post.title} onChange={e => updateField("title", e.target.value)} /></div>
                <div><Label>URL slug</Label><Input value={post.slug} onChange={e => updateField("slug", e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Excerpt</Label><Textarea rows={2} value={post.excerpt || ""} onChange={e => updateField("excerpt", e.target.value)} /></div>
                <div className="sm:col-span-2"><CoverPicker value={post.cover_image} onChange={v => updateField("cover_image", v)} /></div>
                <div className="flex items-center justify-between sm:col-span-2">
                  <Label>Published</Label>
                  <Switch checked={post.published} onCheckedChange={v => updateField("published", v)} />
                </div>
              </div>
            </div>

            <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-serif-display text-2xl">Content blocks</h2>
              <div className="flex gap-2">
                <Select value={addType} onValueChange={setAddType}>
                  <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BLOCK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={addBlock} className="btn-primary-burgundy"><Plus size={14} /> Add</button>
              </div>
            </div>
            <div>
              {(post.blocks || []).map((b, i) => (
                <BlockEditor key={b.id || i} block={b}
                  onChange={(nb) => updateBlock(i, nb)}
                  onDelete={() => deleteBlock(i)}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)} />
              ))}
            </div>
          </div>
        )}

        {viewMode !== "edit" && (
          <div className="min-w-0">
            <div className="sticky top-24">
              <div className="flex items-center gap-2 mb-3">
                <span className="label-eyebrow text-neutral-500">Live preview</span>
                <span className="text-xs text-neutral-400">— updates as you type</span>
              </div>
              <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="bg-neutral-100 border-b border-neutral-200 px-4 py-2 flex items-center gap-2 text-xs text-neutral-500">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                  <span className="ml-2 truncate">/blog/{post.slug}</span>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
                  <div className="px-6 sm:px-8 pt-10 pb-6">
                    <h1 className="font-serif-display text-3xl sm:text-4xl leading-[1.1] text-[#1a1a1a] mb-2">{post.title || "Untitled"}</h1>
                    <p className="text-xs text-neutral-500 tracking-wider">{new Date(post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} · {post.author}</p>
                  </div>
                  {post.cover_image && (
                    <div className="px-6 sm:px-8 mb-6">
                      <img src={fileUrl(post.cover_image)} alt="" className="w-full aspect-[16/9] object-cover" />
                    </div>
                  )}
                  <BlockRenderer blocks={post.blocks || []} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
